import { ProductInterface } from '@qlevr/shared/interfaces';
import { useProduct } from '@qlevr/shared/providers';
import { useActiveOptions } from '@qlevr/shared/utilities';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import ImageRenderer from '../image-renderer/image-renderer';
import clsx from 'clsx';

export interface BundleAndSaveActiveProductProps {
  product: ProductInterface;
}

export function BundleAndSaveActiveProduct({ product }: BundleAndSaveActiveProductProps) {
  const t = useTranslations();
  const { selectedVariant } = useProduct();

  // Extract all active options from search params dynamically
  const activeOptions = useActiveOptions(); // Dynamically get all selected options

  const matchingVariant = useMemo(() => {
    if (!product.variants) return null;

    return product.variants.find((variant) => {
      const optionsMap = new Map(variant.options.map((opt) => [opt.name.toLowerCase(), opt.value]));

      return Object.entries(activeOptions).every(([key, value]) => {
        if (!value) return true; // Skip if the option is not selected
        return optionsMap.get(key) === value;
      });
    });
  }, [product, activeOptions]);

  // Use the selected variant if available, otherwise fallback to matching variant
  const variant = useMemo(() => selectedVariant || matchingVariant, [selectedVariant, matchingVariant]);

  const displayText = useMemo(() => {
    if (!variant) return '';
    return variant.title;
  }, [variant]);

  if (!product) return null;

  const { priceRange, compareAtPriceRange } = product;
  const price = variant?.price;
  const compareAtPrice = variant?.compareAtPrice;

  return (
    <div className="grid grid-flow-col items-center justify-items-end gap-3 bg-white px-10 py-6">
      <div className="flex w-full items-center space-x-4">
        <ImageRenderer loadingState="lazy" image={product.featuredImage} className="w-20" />
        <div>
          <span className="text-base font-bold">{product.title}</span>
          <div className="capitalize">{displayText}</div>
          <div className="flex gap-1">
            {compareAtPrice?.amount && (
              <span className="line-through">
                {compareAtPriceRange?.minVariantPrice.symbol}
                {compareAtPrice.amount}
              </span>
            )}
            <span className={clsx('font-bold', compareAtPrice?.amount && 't-cart-upsell-card-price')}>
              {priceRange?.minVariantPrice.symbol}
              {price?.amount}
            </span>
          </div>
        </div>
      </div>
      <button className="w-fit rounded-full bg-green-500 px-4 py-2 text-sm text-white">
        {t('bundleAndSave.addedToCart')}
      </button>
    </div>
  );
}

export default BundleAndSaveActiveProduct;
